import { Location } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { ENVIRONMENT } from "../../environments/environment";
import { AreaAction } from "../classes/flow/AreaActions/AreaAction";
import { Changes } from "../classes/flow/base/Changes";
import { Entity } from "../classes/flow/base/Entity";
import { Answer } from "../classes/flow/Questionnaire/Answer";
import { OperatorType } from "../classes/flow/Questionnaire/OperatorType";
import { Option } from "../classes/flow/Questionnaire/Option";
import { Question, QuestionReportCategory } from "../classes/flow/Questionnaire/Question";
import { Questionnaire } from "../classes/flow/Questionnaire/Questionnaire";
import { QuestionnaireType } from "../classes/flow/Questionnaire/QuestionnaireType";
import { QuestionType } from "../classes/flow/Questionnaire/QuestionType";
import { ReportCategory } from "../classes/flow/Questionnaire/ReportCategory";
import { Specialty } from "../classes/flow/Questionnaire/Specialty";
import {
  CostPerCategoryStatisticItem,
  FormattedReportCategory,
  ReportCategoryStatisticItem,
  Statistic,
} from "../classes/flow/Questionnaire/Statistic";
import { Validation } from "../classes/flow/Questionnaire/Validation";
import { EnergyConsult } from "../classes/flow/request/EnergyConsult";
import { RequestState, RequestStates } from "../classes/flow/request/RequestStates";
import { Coach } from "../classes/flow/session/impl/Coach";
import { Resident } from "../classes/flow/session/impl/Resident";
import { ErrorMessageComponent } from "../ffwd/components/error-message/error-message.component";
import { SuccessMessageComponent } from "../ffwd/components/success-message/success-message.component";
import { WarningMessageComponent } from "../ffwd/components/warning-message/warning-message.component";
import { Api, ApiService } from "./api.service";
import { ApplicationService } from "./application.service";
import { AreaActionService } from "./area-action.service";
import { NewSnackbarService } from "./snackbar-new.service";
import { convertDate } from "../helpers/convertDate";

@Injectable({
  providedIn: "root",
})
export class EpfService {
  private api = inject(ApiService);
  private areaActions = inject(AreaActionService);
  private snackbar = inject(NewSnackbarService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly applicationService = inject(ApplicationService);

  constructor() {
    const http = inject(HttpClient);
    this.api.apiList.set("epf", new Api(http, ENVIRONMENT.API_URL));
  }

  public async start() {
    const result: { areaActions: AreaAction[]; specialties: Specialty[] } = { areaActions: [], specialties: [] };

    const response = (await firstValueFrom(this.api.getApi("epf").post("webappstart", {}))) as WebAppStartResponse;
    if (response.data) {
      result.areaActions = response.data.areaActions.map((a) => this.parseAreaAction(a));
      result.specialties = response.data.specialties.filter((x) => ENVIRONMENT.ALLOWED_SPECIALTIES.includes(x.id));
    }
    if (response.messages) {
      this.processMessages(response.messages);
    }

    return result;
  }

  public async metadata() {
    const result: {
      questionnaireTypes: QuestionnaireType[];
      questionTypes: QuestionType[];
      reportCategories: ReportCategory[];
      validations: Validation[];
      operatorTypes: OperatorType[];
    } = {
      operatorTypes: [],
      questionnaireTypes: [],
      questionTypes: [],
      reportCategories: [],
      validations: [],
    };

    const response = (await firstValueFrom(this.api.getApi("epf").get("webappmetadata"))) as WebAppMetaDataResponse;
    if (response.messages) {
      this.processMessages(response.messages);
    }
    if (response.data) {
      result.operatorTypes = response.data.metadata.operatorTypes;
      result.questionTypes = response.data.metadata.questionTypes;
      result.questionnaireTypes = response.data.metadata.questionnaireTypes;
      result.reportCategories = response.data.metadata.reportCategories;
      result.validations = response.data.metadata.validations;
    }

    return result;
  }

  public async dashboard(year: string) {
    const result: { energyConsults: EnergyConsult[]; requestStates: RequestState[]; areaActions: AreaAction[]; coaches: Coach[] } = {
      energyConsults: [],
      requestStates: [],
      areaActions: [],
      coaches: [],
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("webappdashboard", { year }))) as WebAppDashboardResponse;
    if (response.data) {
      result.areaActions = response.data.areaActions.map((x) => this.parseAreaAction(x));
      result.requestStates = response.data.requestStates.map((x) => this.parseRequestState(x));
      result.energyConsults = response.data.energyConsults.map((x) => this.parseEnergyConsult(x, result.areaActions));
      result.coaches = response.data.coaches.map((x) => this.parseCoach(x));
    }
    if (response.messages) {
      this.processMessages(response.messages);
    }
    return result;
  }

  public async ckd(x: EnergyConsult) {
    if (!x.changes) throw new Error("No changes object on ec");
    const payload = {
      ...this.retrieveChangesFromObject(x.changes),
      id: x.id,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("ckd", payload))) as EpfResponse<{
      ckdData: { requestIDOld: number; requestIDNew: number };
    }>;

    if (response.messages) {
      this.processMessages(response.messages);
    }
    if (!response.data) throw new Error();

    return response.data;
  }

  public async deleteEnergyConsult(x: EnergyConsult) {
    if (!x.changes) throw new Error("No changes object on ec");
    const payload = {
      ...this.retrieveChangesFromObject(x.changes),
      id: x.id,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("deleteenergyconsult", payload))) as EpfResponse<unknown>;

    if (response.messages) {
      this.processMessages(response.messages);
    }
  }

  public async sendPdfToResident(id: number) {
    const payload = {
      id,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("sendpdftoresident", payload))) as EpfResponse<unknown>;

    if (response.messages) {
      this.processMessages(response.messages);
    }
  }

  public async addQuestionReportCategory(questionID: number, reportCategoryID: number): Promise<QuestionReportCategory> {
    const payload = {
      questionID,
      reportCategoryID,
    };

    const response = (await firstValueFrom(
      this.api.getApi("epf").post("addquestionreportcategory", payload),
    )) as EpfResponse<QuestionReportCategoryResponse>;

    if (response.messages) {
      this.processMessages(response.messages);
    }

    if (!response.data) throw new Error();

    return {
      reportCategory: response.data.reportCategory,
      id: response.data.id,
      changes: this.createChangesObject(response.data),
    };
  }

  public async deleteQuestionReportCategory(x: QuestionReportCategory): Promise<void> {
    if (!x.changes) throw new Error();
    const payload = {
      ...this.retrieveChangesFromObject(x.changes),
      id: x.id,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("deletequestionreportcategory", payload))) as EpfResponse<unknown>;

    if (response.messages) {
      this.processMessages(response.messages);
    }
  }

  public async addEnergyConsult(x: EnergyConsult) {
    const payload: AddEnergyConsultPayload = {
      firstName: x.resident?.firstName ?? "",
      email: x.resident?.email ?? "",
      extraProperties: JSON.stringify(x.extraProperties),
      houseNumber: x.houseNumber,
      houseNumberSuffix: x.houseNumberSuffix ?? "",
      lastName: x.resident?.lastName ?? "",
      message: x.message,
      phoneNumber: x.resident?.phoneNumber ?? "",
      postalCode: x.postalCode,
      requestStateId: x.state.id ?? 0,
      specialtyId: x.specialty.id,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("webappaddenergyconsult", payload))) as EpfResponse<{
      energyConsult: EnergyConsultResponse | null;
    }>;

    if (response.messages) {
      this.processMessages(response.messages);
    }

    return response;
  }

  public async getEnergyConsult(id: number) {
    const result: {
      energyConsult: EnergyConsult | null;
      requestStates: RequestState[];
      areaActions: AreaAction[];
      questionnaires: Questionnaire[];
      coaches: Coach[];
    } = {
      energyConsult: null,
      requestStates: [],
      areaActions: [],
      questionnaires: [],
      coaches: [],
    };
    try {
      const response = (await firstValueFrom(this.api.getApi("epf").get("getenergyconsults", { id }))) as GetEnergyConsultResponse;
      if (response) {
        const consult = response.data?.energyconsult;
        result.areaActions = response.data?.areaActions.map((x) => this.parseAreaAction(x)) ?? [];
        if (consult) {
          result.energyConsult = this.parseEnergyConsult(consult, result.areaActions);
          result.questionnaires =
            consult.specialty.specialtyQuestionnaires?.map((x) => this.parseQuestionnaire(x.questionnaire, [consult.specialty])) ?? [];
        }

        result.requestStates = response.data?.requestStates.map((x) => this.parseRequestState(x)) ?? [];
        result.coaches = response.data?.coaches.map((x) => this.parseCoach(x)) ?? [];
        if (response.messages) {
          this.processMessages(response.messages);
        }
      }
    } catch (error) {
      console.log(error);
    }
    return result;
  }

  public async getConsultsAsCSV(
    options: Partial<{
      requestStateIDS: number[] | null;
      dateFrom: Date | null;
      dateTo: Date | null;
      actionsLinkedToEC: { id: number; aaID: number }[];
    }>,
  ) {
    const payload = {
      requestStateIDS: (options.requestStateIDS ?? []).length > 0 ? options.requestStateIDS : undefined,
      dateFrom: options.dateFrom ? convertDate(options.dateFrom) : undefined,
      dateTo: options.dateTo ? convertDate(options.dateTo) : undefined,
      actionsLinkedToEC: (options.actionsLinkedToEC ?? []).length > 0 ? JSON.stringify(options.actionsLinkedToEC) : undefined,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("getconsultsascsv", payload))) as EpfResponse<{
      content: string;
      fileName: string;
    }>;

    if (response.messages) this.processMessages(response.messages);
    if (!response.data) throw new Error();

    return response.data;
  }

  public async getQuestionnaireAsPDF(questionnaireId: number) {
    const payload = {
      questionnaireId,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").get("getquestionnaireaspdf", payload))) as EpfResponse<{
      content: string;
      fileName: string;
    }>;

    if (response.messages) this.processMessages(response.messages);
    if (!response.data) throw new Error();

    return response.data;
  }

  public async addSpecialtyQuestionnaire(questionnaireId: number, specialtyId: number) {
    const payload = {
      questionnaireId,
      specialtyId,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("addspecialtyquestionnaire", payload))) as EpfResponse<unknown>;

    if (response.messages) this.processMessages(response.messages);
    if (!response.data) throw new Error();

    return response.data;
  }

  public async deleteAllSpecialtyQuestionnaireByQuestionnaire(questionnaireId: number) {
    const payload = {
      questionnaireId,
    };

    const response = (await firstValueFrom(
      this.api.getApi("epf").post("deleteallspecialtyquestionnairebyquestionnaire", payload),
    )) as EpfResponse<unknown>;

    if (response.messages) this.processMessages(response.messages);
    if (!response.data) throw new Error();

    return response.data;
  }

  public async statistics(
    params?: { startDate?: Date; endDate?: Date; areaActionID?: number; municipalityID?: number },
    areaAction?: AreaAction,
  ): Promise<Statistic | null> {
    const response = (await firstValueFrom(
      this.api.getApi("epf").post("webappstatistics", {
        startData: params?.startDate,
        endDate: params?.endDate,
        municipalityID: params?.municipalityID,
        areaActionID: areaAction?.id,
      }),
    )) as WebAppStatisticsResponse;
    console.log(response);

    if (response.data?.statistic) {
      return this.parseStatistic(response.data?.statistic, params, areaAction);
    }
    if (response.messages) {
      this.processMessages(response.messages);
    }

    return null;
  }

  public async updateEnergyConsult(x: EnergyConsult): Promise<EnergyConsult> {
    if (!x.changes) throw new Error();
    let result: EnergyConsult | null = x;
    try {
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("updateenergyconsult", {
          ...this.retrieveChangesFromObject(x.changes),
          id: x.id,
          firstName: x.resident?.firstName || null,
          lastName: x.resident?.lastName || null,
          phoneNumber: x.resident?.phoneNumber || null,
          email: x.resident?.email || null,
          appointmentDate: x.appointmentDate || null,
          coachId: x.coach?.id || null,
          disapprovalReason: x.disapprovalReason || null,
          extraProperties: JSON.stringify(x.extraProperties),
          houseNumber: x.houseNumber,
          houseNumberSuffix: x.houseNumberSuffix || null,
          specialtyId: x.specialty.id,
          residentId: x.resident?.id ?? 0,
          postalCode: x.postalCode,
          message: x.message,
          rejectReason: x.rejectReason || null,
          requestStateId: x.state.id ?? 0,
          lastSendDate: x.lastSendDate || null,
          sendResidentMail: null,
        } satisfies UpdateEnergyConsultPayload),
      )) as UpdateEnergyConsultResponse;

      if (response.messages) {
        this.processMessages(response.messages);
      }

      if (response.data?.energyConsult) {
        result = this.parseEnergyConsult(response.data.energyConsult, []);
      }
      // if (!result) throw new Error("Unable to parse energyconsult from response");

      return result;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async updateQuestion(questionnaireId: number, x: Question): Promise<Entity> {
    if (!x.changes) throw new Error();
    try {
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("updatequestion", {
          ...this.retrieveChangesFromObject(x.changes),
          active: x.active,
          compareAnswer: x.logic?.compareAnswer ?? null,
          compareOptionId: x.logic?.compareOptionId ?? null,
          description: x.description,
          extraProperties: JSON.stringify(x.extraProperties) === "{}" ? null : JSON.stringify(x.extraProperties),
          id: x.id ?? 0,
          index: x.index ?? null,
          insertTime: null,
          name: x.name,
          operatorTypeId: x.logic?.operatorType?.id ?? null,
          parentQuestionId: x.logic?.parentQuestionId ?? null,
          placeholder: x.placeholder,
          questionnaireId,
          questionTypeId: x.questionType.id ?? null,
          required: x.required,
          validationId: x.validation?.id ?? null,
        } satisfies UpdateQuestionPayload),
      )) as UpdateQuestionResponse;

      if (response.messages) {
        this.processMessages(response.messages);
      }
      if (!response.data) {
        throw new Error();
      }

      // if (response.data.energyConsult) {
      //   result = this.parseEnergyConsult(response.data.energyConsult, []);
      // }
      // if (!result) throw new Error("Unable to parse energyconsult from response");

      return {
        id: response.data.id,
        changes: this.createChangesObject(response.data),
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async addQuestion(questionnaireId: number, x: Question): Promise<Entity> {
    try {
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("addquestion", {
          active: x.active,
          compareAnswer: x.logic?.compareAnswer ?? null,
          compareOptionId: x.logic?.compareOptionId ?? null,
          description: x.description,
          extraProperties: JSON.stringify(x.extraProperties) === "{}" ? null : JSON.stringify(x.extraProperties),
          index: x.index ?? null,
          insertTime: null,
          name: x.name,
          operatorTypeId: x.logic?.operatorType?.id ?? null,
          parentQuestionId: x.logic?.parentQuestionId ?? null,
          placeholder: x.placeholder,
          questionnaireId,
          questionTypeId: x.questionType.id ?? null,
          required: x.required,
          validationId: x.validation?.id ?? null,
        } satisfies AddQuestionPayload),
      )) as UpdateQuestionResponse;

      if (response.messages) {
        this.processMessages(response.messages);
      }
      if (!response.data) {
        throw new Error();
      }

      // if (response.data.energyConsult) {
      //   result = this.parseEnergyConsult(response.data.energyConsult, []);
      // }
      // if (!result) throw new Error("Unable to parse energyconsult from response");

      return {
        id: response.data.id,
        changes: this.createChangesObject(response.data),
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async deleteQuestion(x: Question): Promise<void> {
    if (!x.changes || !x.id) throw new Error();
    try {
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("deletequestion", {
          ...this.retrieveChangesFromObject(x.changes),
          id: x.id,
        }),
      )) as EpfResponse<unknown>;

      if (response.messages) {
        this.processMessages(response.messages);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async addQuestionOption(x: Option): Promise<Option> {
    try {
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("addquestionoption", {
          questionId: x.questionId ?? 0,
          value: x.value || "",
        }),
      )) as EpfResponse<ChangesResponse & { id: number; value: string; questionId: number }>;

      if (response.messages) {
        this.processMessages(response.messages);
      }

      if (!response.data) throw new Error();

      return {
        value: response.data.value,
        changes: this.createChangesObject(response.data),
        id: response.data.id,
        questionId: response.data.questionId,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async updateQuestionOption(x: Option): Promise<Option> {
    try {
      if (!x.changes || !x.id) throw new Error();
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("updatequestionoption", {
          questionId: x.questionId ?? 0,
          value: x.value || "",
          id: x.id,
          ...this.retrieveChangesFromObject(x.changes),
        }),
      )) as EpfResponse<ChangesResponse & { id: number; value: string; questionId: number }>;

      if (response.messages) {
        this.processMessages(response.messages);
      }

      if (!response.data) throw new Error();

      return {
        value: response.data.value,
        changes: this.createChangesObject(response.data),
        id: response.data.id,
        questionId: response.data.questionId,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async deleteQuestionOption(x: Option): Promise<void> {
    try {
      if (!x.changes || !x.id) throw new Error();
      const response = (await firstValueFrom(
        this.api.getApi("epf").post("deletequestionoption", {
          id: x.id,
          ...this.retrieveChangesFromObject(x.changes),
        }),
      )) as EpfResponse<unknown>;

      if (response.messages) {
        this.processMessages(response.messages);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async reportAsPdf(energyConsultId: number): Promise<string | null> {
    const response = (await firstValueFrom(
      this.api.getApi("epf").post("webappreportaspdf", {
        energyConsultId,
      }),
    )) as ReportAsPdfResponse;

    this.processMessages(response.messages);

    return response.data?.content || "";
  }

  public async sendNewReportCounts(): Promise<EpfResponse<unknown>> {
    const response = (await firstValueFrom(this.api.getApi("epf").get("sendnewreportcounts"))) as EpfResponse<unknown>;

    this.processMessages(response.messages);

    return response;
  }

  public async addQuestionAnswer(answer: Answer) {
    const payload = {
      answer: answer.answer || null,
      energyConsultId: answer.energyConsultId ?? 0,
      questionId: answer.questionId ?? 0,
    } satisfies AddQuestionAnswerPayload;

    const response = (await firstValueFrom(this.api.getApi("epf").post("addquestionanswer", payload))) as AddQuestionAnswerResponse;
    if (response.messages) {
      this.processMessages(response.messages);
    }

    if (response.data?.questionAnswer) {
      return this.parseQuestionAnswer(response.data.questionAnswer);
    }

    return null;
  }

  public async updateQuestionAnswer(answer: Answer) {
    if (!answer.changes) throw new Error("Answer object has no changes object");

    const payload = {
      answer: answer.answer || null,
      energyConsultId: answer.energyConsultId ?? 0,
      questionId: answer.questionId ?? 0,
      id: answer.id ?? 0,
      ...this.retrieveChangesFromObject(answer.changes),
    } satisfies UpdateQuestionAnswerPayload;

    const response = (await firstValueFrom(this.api.getApi("epf").post("updatequestionanswer", payload))) as UpdateQuestionAnswerResponse;
    if (response.messages) {
      this.processMessages(response.messages);
    }

    if (response.data?.questionAnswer) {
      return this.parseQuestionAnswer(response.data.questionAnswer);
    }

    return null;
  }

  public async addQuestionnaire(q: Questionnaire) {
    const payload = {
      active: q.active,
      description: q.description,
      questionnaireTypeId: q.questionnaireType.id ?? 0,
      specialtyQuestionnaires: q.specialties.map((x) => x.id),
      title: q.title,
    } satisfies AddQuestionnairePayload;

    const response = (await firstValueFrom(this.api.getApi("epf").post("addquestionnaire", payload))) as EpfResponse<unknown>;
    console.log(response);
    if (response.messages) {
      this.processMessages(response.messages);
    }
  }

  public async updateQuestionnaire(q: Questionnaire) {
    if (!q.changes || !q.id) throw new Error("Unable to update questionnaire, no changes or id");
    const payload = {
      active: q.active,
      description: q.description,
      questionnaireTypeId: q.questionnaireType.id ?? 0,
      title: q.title,
      ...this.retrieveChangesFromObject(q.changes),
      id: q.id,
      sortorder: q.sortOrder ?? 0,
    } satisfies UpdateQuestionnairePayload;

    const response = (await firstValueFrom(this.api.getApi("epf").post("updatequestionnaire", payload))) as EpfResponse<QuestionnaireResponse>;
    if (response.messages) {
      this.processMessages(response.messages);
    }

    if (response.data) {
      q.active = response.data.active;
      q.description = response.data.description;
      q.isAnswered = response.data.isAnswered;
      q.sortOrder = response.data.sortorder;
      q.changes = this.createChangesObject(response.data);
    }
  }

  public async getQuestionnaires(specialties: Specialty[], id?: number) {
    const payload = id ? { id } : undefined;

    const response = (await firstValueFrom(this.api.getApi("epf").get("getquestionnaires", payload))) as GetQuestionnairesResponse;
    return response.data?.questionnaires.map((x) => {
      const questionnaireSpecialties = specialties.filter((s) => x.specialtyQuestionnaires.some((x) => x.specialtyId === s.id));
      return this.parseQuestionnaire(x, questionnaireSpecialties);
    });
  }

  public async copyQuestionnaire(q: Questionnaire) {
    if (!q.id) throw new Error("Unable to update questionnaire, no id");
    const payload = {
      description: q.description,
      title: q.title,
      id: q.id,
    };

    const response = (await firstValueFrom(this.api.getApi("epf").post("copyquestionnaire", payload))) as EpfResponse<QuestionnaireResponse>;
    if (response.messages) {
      this.processMessages(response.messages);
    }
  }

  public static isErrorResponse(x: EpfResponse<unknown>): boolean {
    return x.messages.some((x) => x.type === 3);
  }

  private createChangesObject(x: ChangesResponse): Changes {
    return {
      fullDetails: new Map([
        ["1", { userId: x?.lastChange.userId, time: x?.lastChange.time ? x?.lastChange.time : undefined }],
        ["0", { userId: x?.create.userId, time: x?.create.time ? x?.create.time : undefined }],
      ]),
      lastChange: { userId: x?.lastChange.userId, time: x?.lastChange.time ? x?.lastChange.time : undefined },
    };
  }

  private retrieveChangesFromObject(x: Changes): ChangesResponse {
    const create = x.fullDetails?.get("0");
    return {
      create: {
        time: typeof create?.time === "string" ? create?.time : null,
        userId: create?.userId ?? 0,
      },
      lastChange: {
        time: typeof x.lastChange?.time === "string" ? x.lastChange?.time : null,
        userId: x.lastChange?.userId ?? 0,
      },
    };
  }

  private parseAreaAction(a: AreaActionResponse): AreaAction {
    return new AreaAction({
      id: a.id,
      dateFrom: new Date(a.dateFrom),
      dateTo: new Date(a.dateTo),
      zipCodeFrom: a.zipCodeFrom,
      zipCodeTo: a.zipCodeTo,
      actionType: a.actionType,
      area: a.area,
      extraProperties: a.extraProperties ? JSON.parse(a.extraProperties) : {},
      changes: this.createChangesObject(a),
      expired: a.expired ?? undefined,
      municipality: a.municipality,
    });
  }

  private parseQuestionReportCategory(x: QuestionReportCategoryResponse): QuestionReportCategory {
    return {
      changes: this.createChangesObject(x),
      reportCategory: x.reportCategory,
      id: x.id,
    };
  }

  private parseCoach(data: CoachResponse): Coach {
    return new Coach({
      id: data.id,
      userEmail: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    });
  }

  private parseEnergyConsult(data: EnergyConsultResponse, allAreaActions: AreaAction[]): EnergyConsult {
    const coach = data.coach ? this.parseCoach(data.coach) : undefined;

    const specialty: Specialty = {
      id: data.specialty.id,
      name: data.specialty.name,
      description: data.specialty.description,
      order: data.specialty.order,
    };

    const resident = new Resident({
      id: data.resident?.id || data.residentId || -1,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      email: data.email,
    });

    const state = this.parseRequestState(data.requestState);
    if (!state) throw new Error(` Unknown request state => ${data.requestStateId}`);

    const areaActions = allAreaActions.filter((a) => {
      return this.areaActions.zipCodeInArea(data.postalCode, a);
    });

    const ec = new EnergyConsult({
      id: data.id,
      message: data.message,
      postalCode: data.postalCode,
      houseNumber: data.houseNumber,
      requestDate: data.requestDate || "",
      state,
      specialty,
      resident,
      houseNumberSuffix: data.houseNumberSuffix,
      lastStateChangeDate: data.statusLastChanged || "",
      rejectReason: data.rejectReason,
      coach: coach,
      appointmentDate: data.appointmentDate || "",
      disapprovalReason: data.disapprovalReason,
      changes: this.createChangesObject(data),
      extraProperties: JSON.parse(data.extraProperties),
      areaActions,
      ckd: data.ckd,
      ckdInfo: data.ckdInfo,
      mailHistoryId: data.mailhistoryactionid,
      bookmarked: data.bookmarked ?? false,
      lastSendDate: data.lastSendDate || "",
      sendResidentMail: data.sendResidentMail ?? undefined,
      ffwdContentId: data.ffwdcontentid,
      ffwdContentPartId: data.ffwdcontentpartid,
    });
    ec.attachments = data.attachments ?? [];

    return ec;
  }

  private parseRequestState(data: RequestStateResponse): RequestState {
    return {
      name: data.name.trim() as RequestStates,
      changes: this.createChangesObject(data),
      id: data.id,
    };
  }

  private parseStatistic(
    value: StatisticResponse,
    params?: { startDate?: Date; endDate?: Date; areaActionID?: number },
    areaAction?: AreaAction,
  ): Statistic {
    /**
     * set status
     */
    const currentDate = new Date();
    let status: "TOTAL" | "FUTURE" | "CURRENT" | "PAST" = "TOTAL";
    if (params) {
      if (params.endDate && currentDate.getTime() > params.endDate.getTime()) {
        status = "PAST";
      } else if (params.startDate && currentDate.getTime() > params.startDate.getTime()) {
        status = "CURRENT";
      } else {
        status = "FUTURE";
      }

      if (params.areaActionID !== undefined && areaAction !== undefined) {
        if (currentDate.getTime() > areaAction.dateTo.getTime()) {
          status = "PAST";
        } else if (currentDate.getTime() > areaAction.dateFrom.getTime()) {
          status = "CURRENT";
        } else {
          status = "FUTURE";
        }
      }
    }

    /**
     * format report categories
     */
    const parsedReportCategoryStatitics: ReportCategoryStatisticItem[] = value.reportCategoryStatistics
      ? JSON.parse(value.reportCategoryStatistics)
      : [];

    const parsedCostPerCategoryStatitics: CostPerCategoryStatisticItem[] = value.costPerCategoryStatistics
      ? JSON.parse(value.costPerCategoryStatistics)
      : [];

    const formatted: FormattedReportCategory[] = [];

    for (const element of parsedReportCategoryStatitics) {
      if (!formatted.some((frc) => frc.id === element.reportCategoryID)) {
        formatted.push({
          id: element.reportCategoryID,
          name: element.reportCategoryName,
          description: element.reportCategoryDescription,
          type: element.bs5Value ? "select" : "cost",
          selectValues: element.bs5Value
            ? [
                {
                  answeredCount: element.answeredCount,
                  value: element.bs5Value,
                  cost:
                    parsedCostPerCategoryStatitics.find(
                      (costItem) => costItem.reportCategoryID === element.reportCategoryID && costItem.bs5Value === element.bs5Value,
                    )?.cost ?? 0,
                },
              ]
            : undefined,
          costValues: !element.bs5Value
            ? [{ count: element.answeredCount, totalPrice: element.totalPrice!, percentageOfTotal: element.percentageOfTotal! }]
            : undefined,
        });
      } else {
        if (element.bs5Value) {
          formatted
            .find((frc) => frc.id === element.reportCategoryID)
            ?.selectValues?.push({
              answeredCount: element.answeredCount,
              value: element.bs5Value,
              cost:
                parsedCostPerCategoryStatitics.find(
                  (costItem) => costItem.reportCategoryID === element.reportCategoryID && costItem.bs5Value === element.bs5Value,
                )?.cost ?? 0,
            });
        } else {
          formatted
            .find((frc) => frc.id === element.reportCategoryID)
            ?.costValues?.push({ count: element.answeredCount, totalPrice: element.totalPrice!, percentageOfTotal: element.percentageOfTotal! });
        }
      }
    }

    /**
     * return
     */
    return {
      status: status,
      count: value.count,
      averageTimeInDays: value.averageTimeInDays,
      averageTimeInHours: value.averageTimeInHours,
      totalPrice: value.totalPrice,
      averagePrice: value.averagePrice,
      specialtyStatistics: value.specialtyStatistics ? JSON.parse(value.specialtyStatistics) : [],
      stateStatistics: value.stateStatistics ? JSON.parse(value.stateStatistics) : [],
      coachStatistics: value.coachStatistics ? JSON.parse(value.coachStatistics) : [],
      reportCategoryStatistics: parsedReportCategoryStatitics,
      formatedReportCategories: formatted,
      costPerCategoryStatistics: parsedCostPerCategoryStatitics,
      selection: {
        areaActionID: params?.areaActionID ?? null,
        areaAction: areaAction ?? null,
        timeRange: areaAction
          ? { start: areaAction.dateFrom, end: areaAction.dateTo }
          : params?.endDate && params.startDate
            ? { start: params.startDate, end: params.endDate }
            : null,
      },
    };
  }

  public processMessages(messages: MessageResponse[]): void {
    let refresh = false;
    let block = false;
    let previous = false;
    let redirect: string | null = null;

    for (const message of messages) {
      for (const key in message) {
        const first = key.substring(0, 1);
        const rest = key.substring(1);
        const newEntry = first.toLowerCase() + rest;
        if (newEntry != key) message[newEntry] = message[key];
      }

      if (message.refresh) refresh = true;
      if (message.back) previous = true;
      if (message.goToLink) redirect = message.linkToGoTo;
      if (message.block) block = true;

      switch (message.type) {
        case 1:
        case 4:
          this.snackbar.open<string>(SuccessMessageComponent, message.message);
          break;

        case 2:
          this.snackbar.open<string>(WarningMessageComponent, message.message);
          break;

        case 3:
          this.snackbar.open<string>(ErrorMessageComponent, message.message);
          break;

        default:
          throw new Error(`Unhandeled message type => ${message.type}`);
      }
    }

    // TODO:
    // if (block) {
    //   this.blocked.next(true);
    // } else {
    //   if (redirect) {
    //     this.router.navigate([`app/content/${redirect}`]);
    //   } else if (refresh) {
    //     this.refresh();
    //   } else if (previous) {
    //     this.splitscreen.value?.type === "reading-pane" ? this.splitscreen.next(null) : this.history.previous();
    //   }
    // }

    if (block) {
      this.applicationService.blocked = true;
    } else if (redirect) {
      this.router.navigate([redirect]);
    } else if (refresh) {
      location.reload();
    } else if (previous) {
      this.location.back();
    }
  }

  private parseQuestionnaire(q: QuestionnaireResponse, specialties?: Specialty[]): Questionnaire {
    return {
      active: q.active,
      description: q.description,
      questionnaireType: q.questionnaireType,
      sortOrder: q.sortorder,
      specialties: specialties ?? [],
      title: q.title,
      changes: this.createChangesObject(q),
      id: q.id,
      isAnswered: q.isAnswered,
      questions: q.questions.map((x) => this.parseQuestion(x)),
    };
  }

  private parseQuestion(x: QuestionResponse): Question {
    const answer = x.questionAnswers.at(0);

    return {
      active: x.active,
      description: x.description,
      extraProperties: x.extraProperties ? JSON.parse(x.extraProperties) : {},
      followUpQuestions: [], // TODO:
      name: x.name,
      placeholder: x.placeholder,
      questionType: x.questionType,
      required: x.required,
      answer: answer ? this.parseQuestionAnswer(answer) : undefined,
      changes: this.createChangesObject(x),
      id: x.id,
      index: x.index,
      logic:
        x.parentQuestionId && (x.compareAnswer || x.compareOptionId)
          ? {
              parentQuestionId: x.parentQuestionId,
              compareAnswer: x.compareAnswer,
              compareOptionId: x.compareOptionId,
              compareOptionReference: undefined, // TODO:
              operatorType: undefined, // TODO:
            }
          : undefined,
      options: x.questionOptions.map((_) => this.parseQuestionOption(_)),
      reportCategories: x.questionReportCategories.map((_) => this.parseQuestionReportCategory(_)),
      validation: x.validation,
    };
  }

  private parseQuestionAnswer(x: QuestionAnswerResponse): Answer {
    return { answer: x.answer, changes: this.createChangesObject(x), energyConsultId: x.energyConsultId, questionId: x.questionId, id: x.id };
  }

  private parseQuestionOption(x: QuestionOptionResponse): Option {
    return {
      value: x.value,
      changes: this.createChangesObject(x),
      id: x.id,
      questionId: x.questionId,
    };
  }
}

type EpfResponse<T> = {
  messages: MessageResponse[];
  result: string;
  sessionstate: string;
  refreshSession: string;
  data?: T;
};

type WebAppStartResponse = EpfResponse<{
  areaActions: AreaActionResponse[];
  specialties: SpecialtyResponse[];
}>;

type WebAppDashboardResponse = EpfResponse<{
  coordinators: unknown[];
  energyConsults: EnergyConsultResponse[];
  municipalities: MunicipalityResponse[];
  requestStates: RequestStateResponse[];
  areaActions: AreaActionResponse[];
  specialties: SpecialtyResponse[];
  coaches: CoachResponse[];
}>;

type GetEnergyConsultResponse = EpfResponse<{
  energyconsult?: EnergyConsultResponse;
  requestStates: RequestStateResponse[];
  areaActions: AreaActionResponse[];
  coaches: CoachResponse[];
}>;

type WebAppStatisticsResponse = EpfResponse<{
  statistic?: StatisticResponse;
}>;

type AreaActionResponse = ChangesResponse & {
  actionType: string;
  area: string;
  dateFrom: Date;
  dateTo: Date;
  expired: boolean | null;
  id: number;
  extraProperties: string;
  municipality: string;
  zipCodeFrom: string;
  zipCodeTo: string;
};

type SpecialtyResponse = ChangesResponse & {
  description: string;
  id: number;
  name: string;
  order: number;
};

type ChangesResponse = {
  create: { userId: number; time: string | null };
  lastChange: { userId: number; time: string | null };
};

type AddEnergyConsultPayload = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  message: string;
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix: string;
  specialtyId: number;
  requestStateId: number;
  extraProperties: string;
};

type CoachResponse = ChangesResponse & {
  active: boolean;
  coachSpecialities: unknown[];
  email: string;
  emailSubscription: boolean;
  firstName: string;
  id: number;
  lastName: string;
  registered: boolean;
  toegangDatumTM: unknown | null;
  toegangDatumVanaf: unknown | null;
  userId: number;
};

type EnergyConsultResponse = ChangesResponse & {
  attachments?: AttachmentResponse[];
  appointmentDate: string | null;
  bookmarked: boolean;
  ckd: boolean;
  ckdInfo: string;
  coach: CoachResponse | null;
  coachId: number;
  disapprovalReason: string;
  email: string;
  extraProperties: string;
  ffwdcontentid: string;
  ffwdcontentpartid: string;
  firstName: string;
  houseNumber: number;
  houseNumberSuffix: string;
  id: number;
  jsnExtraProperties: string;
  lastName: string;
  lastSendDate: null;
  mailhistoryactionid: string;
  message: string;
  phoneNumber: string;
  postalCode: string;
  questionAnswers: [];
  rejectReason: string;
  requestDate: string | null;
  requestState: RequestStateResponse;
  requestStateId: number;
  resident: { id: number } | null;
  residentId: number;
  sendResidentMail: null;
  specialty: {
    id: number;
    name: string;
    description: string;
    order: number;
    specialtyQuestionnaires?: SpecialtyQuestionnaireResponse[];
  };
  specialtyId: number;
  statusLastChanged: null;
  streetName: string;
  year: string;
};

type RequestStateResponse = ChangesResponse & {
  active: boolean;
  id: number;
  name: string;
  order: number;
};

type MunicipalityResponse = ChangesResponse & {
  cdpCode: string;
  cdpProvincieCode: string;
  gtpCode: number;
  gtpJaar: number;
  gtpProvincieCode: number;
  id: number;
  name: string;
  omlProvincie: string;
};

type StatisticResponse = ChangesResponse & {
  count: number;
  averageTimeInDays: number;
  averageTimeInHours: number;
  totalPrice: number;
  averagePrice: number;
  specialtyStatistics: null;
  stateStatistics: null;
  coachStatistics: null;
  reportCategoryStatistics: null;
  costPerCategoryStatistics: null;
  id: number;
};

type UpdateEnergyConsultPayload = ChangesResponse & {
  id: number;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  email: string | null;
  message: string | null;
  postalCode: string | null;
  houseNumber: number;
  houseNumberSuffix: string | null;
  rejectReason: string | null;
  disapprovalReason: string | null;
  appointmentDate: string | null;
  lastSendDate: string | null;
  coachId: number | null;
  residentId: number;
  specialtyId: number;
  requestStateId: number;
  extraProperties: string;
  sendResidentMail: boolean | null;
};

type UpdateEnergyConsultResponse = EpfResponse<{
  energyConsult: EnergyConsultResponse;
}>;

export type MessageResponse = {
  number: number;
  type: number;
  undo: boolean;
  refresh: boolean;
  block: boolean;
  back: boolean;
  goToLink: boolean;
  lc: string;
  message: string;
  linkToGoTo: string;
};

type ReportAsPdfResponse = EpfResponse<{
  content: string;
  fileName: string;
  sto: string;
}>;

type SpecialtyQuestionnaireResponse = ChangesResponse & {
  specialtyId: number;
  questionnaireId: number;
  id: number;
  questionnaire: QuestionnaireResponse;
};

type QuestionnaireResponse = ChangesResponse & {
  id: number;
  active: boolean;
  title: string;
  description: string;
  questionnaireTypeId: number;
  isAnswered: boolean;
  sortorder: number;
  questionnaireType: ChangesResponse & {
    name: string;
    order: number;
    id: number;
  };
  questions: QuestionResponse[];
  specialtyQuestionnaires: { specialtyId: number }[];
};

type QuestionResponse = ChangesResponse & {
  validation: ValidationResponse;
  name: string;
  active: boolean;
  insertTime: string;
  description: string;
  compareAnswer: string;
  index: string;
  placeholder: string;
  required: boolean;
  parentQuestionId: number;
  compareOptionId: number;
  questionTypeId: number;
  questionType: QuestionTypeResponse;
  operatorTypeId: number | null;
  operatorType: unknown;
  questionOptions: QuestionOptionResponse[];
  validationId: number;
  extraProperties: string | null;
  parentQuestion: unknown;
  childQuestions: unknown;
  questionReportCategories: QuestionReportCategoryResponse[];
  id: number;
  questionAnswers: QuestionAnswerResponse[];
};

type ValidationResponse = ChangesResponse & {
  name: string;
  id: number;
};

type QuestionTypeResponse = ChangesResponse & {
  name: string;
  prefix: string;
  id: number;
};

type QuestionAnswerResponse = ChangesResponse & {
  answer: string;
  questionId: number;
  energyConsultId: number;
  id: number;
};

type AddQuestionAnswerPayload = {
  answer: string | null;
  questionId: number;
  energyConsultId: number;
};

type UpdateQuestionAnswerPayload = ChangesResponse & {
  id: number;
  answer: string | null;
  questionId: number;
  energyConsultId: number;
};

type AddQuestionAnswerResponse = EpfResponse<{ questionAnswer: QuestionAnswerResponse | null }>;
type UpdateQuestionAnswerResponse = EpfResponse<{ questionAnswer: QuestionAnswerResponse | null }>;

type QuestionOptionResponse = ChangesResponse & {
  id: number;
  value: string;
  questionId: number;
};

export type AttachmentResponse = ChangesResponse & {
  id: number;
  name: string;
  extension: string;
  actionIDGet: string;
  actionIDDelete: string;
  idzRecordId: number;
  fileSize: number;
  idfOrganisatienivoIDBestand: number;
};

type OperatorTypeResponse = ChangesResponse & {
  id: number;
  name: string;
  operation: string;
};

type QuestionnaireTypeResponse = ChangesResponse & {
  id: number;
  name: string;
  order: number;
};

type ReportCategoryResponse = ChangesResponse & {
  id: number;
  active: boolean;
  description: string;
  name: string;
};

type WebAppMetaDataResponse = EpfResponse<{
  metadata: {
    operatorTypes: OperatorTypeResponse[];
    questionTypes: QuestionTypeResponse[];
    questionnaireTypes: QuestionnaireTypeResponse[];
    reportCategories: ReportCategoryResponse[];
    validations: ValidationResponse[];
  };
}>;

type AddQuestionnairePayload = {
  active: boolean;
  title: string;
  description: string;
  questionnaireTypeId: number;
  specialtyQuestionnaires: number[];
};

type UpdateQuestionnairePayload = ChangesResponse & {
  id: number;
  active: boolean;
  title: string;
  description: string;
  questionnaireTypeId: number;
  sortorder: number;
};

type GetQuestionnairesResponse = EpfResponse<{
  questionnaires: QuestionnaireResponse[];
}>;

type UpdateQuestionPayload = ChangesResponse & {
  id: number;
  name: string | null;
  active: boolean | null;
  insertTime: string | null;
  description: string | null;
  compareAnswer: string | null;
  index: string | null;
  placeholder: string | null;
  required: boolean | null;
  parentQuestionId: number | null;
  questionnaireId: number;
  compareOptionId: number | null;
  questionTypeId: number | null;
  operatorTypeId: number | null;
  validationId: number | null;
  extraProperties: string | null;
};

type AddQuestionPayload = {
  name: string | null;
  active: boolean | null;
  insertTime: string | null;
  description: string | null;
  compareAnswer: string | null;
  index: string | null;
  placeholder: string | null;
  required: boolean | null;
  parentQuestionId: number | null;
  questionnaireId: number;
  compareOptionId: number | null;
  questionTypeId: number | null;
  operatorTypeId: number | null;
  validationId: number | null;
  extraProperties: string | null;
};

export type QuestionReportCategoryResponse = ChangesResponse & {
  questionID: number;
  reportCategoryID: number;
  reportCategory: ReportCategoryResponse;
  id: number;
};

type UpdateQuestionResponse = EpfResponse<ChangesResponse & { id: number }>;
